import { Component } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Banner } from '@spotify-internal/encore-web';

import * as Sentry from 'lib/client/sentry/sentry';
import withTranslation from 'lib/shared/i18n/withTranslation';
import { isValidSpotifyUri } from 'components/ContentReporting/validation-utils';
import { getFieldValid, getFieldValue, getUriQueryParam } from 'components/ContentReporting/form-utils';
import { withMarketInfo } from 'context/MarketInfo/MarketInfoProvider';
import { Field } from 'components/ContentReporting/ContentPolicyReporting/content-policy-reporting-config';
import Step1Description from 'components/ContentReporting/ContentPolicyReporting/Step1Description';
import Step2ExternalReportedContent from 'components/ContentReporting/ContentPolicyReporting/Step2ExternalReportedContent';
import EmailVerificationDialog from 'components/ContentReporting/EmailVerification/EmailVerificationDialog';
import { sendPageview } from 'utils/metrics';
import { FieldChangeData, FormData, MarketInfoProps, TranslationProps, UserInfoProps } from 'etc/types';
import { contentTypeFromJSON, EmailType, ReportOriginType } from 'proto/generated/tintin_content_policy';
import { TintinClientProps, withTintinClient } from 'components/ContentReporting/withTintinClient';
import Step2EmployeeReportedContent from 'components/ContentReporting/ContentPolicyReporting/Step2EmployeeReportedContent';
import { ContextualInfoProps, withContextualInfo } from 'components/ContentReporting/withContextualInfo';
import { FASTER_EMAIL_VERIFICATION } from 'lib/shared/config/config';
import { withUserInfo } from 'context/UserInfo/UserInfoProvider';
import RateLimitingDialog from 'components/ContentReporting/RateLimiting/RateLimitingDialog';
import { getThankYouPagePathName } from 'utils/functions';
import { ExpetimentationProps, withExperimentation } from 'context/Experimentation/ExperimentationProvider';
import { entityTypeFromJSON } from 'proto/generated/inspector_gadget';
import LoggedInBanner from 'components/ContentReporting/LoggedInNotification/LoggedInBanner';

import { couldHaveAssociatedContent } from '../ContentLegalReporting/utils/functions';

import { ErrorMessage, Step } from './utils/types';
import { ACCEPTED_URI_TYPES_ALL } from './utils/constants';
import { CONTENT_TYPE_MAPPER } from './utils/constants/contentType';
import {
  buildEmployeeRequest,
  buildExternalRequest,
  getPayloadBasedOnVerificationStep,
  getPayloadBasedUserType,
} from './utils/functions/reportSubmit';

interface ContentPolicyReportingProps
  extends RouteComponentProps,
    MarketInfoProps,
    TranslationProps,
    TintinClientProps,
    ContextualInfoProps,
    UserInfoProps,
    ExpetimentationProps {
  formData: FormData;
  transitionTime?: number;
}

interface VerificationDetails {
  resourceUri?: string;
  code?: string;
}

interface ContentPolicyReportingState {
  currentStep: Step;
  formData: FormData;
  shouldDisplayEmailVerificationFlow: boolean;
  shouldDisplayRateLimitError: boolean;
  shouldDisplayUserCountryError: boolean;
  forceValidation: boolean;
  formSubmitting: boolean;
  formSubmitted: boolean;
  formSubmissionError: ErrorMessage | '';
  formSubmissionSuccess: boolean;
  loading: boolean;
  verificationDetails: VerificationDetails;
}

export const validateFieldsOnSubmit = (
  fields: Array<Field>,
  fieldValue: Function,
  fieldValid: Function,
): Array<Field> => {
  const isValuePresent: (field: Field) => boolean = (field: Field) => !!fieldValue(field);
  const isValueValid: (field: Field) => boolean = (field: Field) => fieldValid(field);
  const invalidFields: Array<Field> = fields.filter((field: Field) => {
    return !isValuePresent(field) || !isValueValid(field);
  });
  return invalidFields;
};

class ContentPolicyReporting extends Component<ContentPolicyReportingProps, ContentPolicyReportingState> {
  static defaultProps = {
    formData: {},
  };

  constructor(props: ContentPolicyReportingProps) {
    super(props);
    const uriQueryParam = getUriQueryParam(props.location) as string;
    this.state = {
      currentStep: Step.STEP1_DESCRIPTION,
      formData: {
        ...this.props.formData,
        [Field.REPORTED_CONTENT_URIS]: {
          value: uriQueryParam,
          valid: isValidSpotifyUri(uriQueryParam, ACCEPTED_URI_TYPES_ALL),
        },
      },
      shouldDisplayEmailVerificationFlow: false,
      shouldDisplayRateLimitError: false,
      shouldDisplayUserCountryError: false,
      forceValidation: false,
      formSubmitting: false,
      formSubmitted: false,
      formSubmissionError: '',
      formSubmissionSuccess: false,
      loading: false,
      verificationDetails: {},
    };

    this.props.userInfo.setReportOriginType(ReportOriginType.REPORT_ORIGIN_POLICY);
    Sentry.setTag('app', 'ContentPolicyReporting');
    sendPageview(`content-policy-reporting`);
  }

  handleFieldChange = ({ fieldIdentifier, value, valid }: FieldChangeData) => {
    this.setState(
      (prevState: Readonly<ContentPolicyReportingState>): ContentPolicyReportingState => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          [fieldIdentifier]: {
            value,
            valid,
          },
        },
        formSubmissionError: valid ? '' : prevState.formSubmissionError,
      }),
    );
  };

  handleFormSubmit = async (formData: FormData, isInternalReport: boolean): Promise<void> => {
    const fieldValue: (field: Field) => string = getFieldValue(formData);
    const fieldValid: (field: Field) => boolean = getFieldValid(formData);
    const contentTypeValue = fieldValue(Field.REPORTED_CONTENT_TYPE);

    const requiredPayloadFields = [
      ...getPayloadBasedUserType(isInternalReport),
      ...getPayloadBasedOnVerificationStep(!!fieldValue(Field.VERIFICATION_RESOURCE_URI)),
    ];

    const convertedContentTypeValue = contentTypeValue
      ? CONTENT_TYPE_MAPPER[contentTypeFromJSON(contentTypeValue)]
      : undefined;

    const fields =
      !!convertedContentTypeValue &&
      couldHaveAssociatedContent(convertedContentTypeValue, entityTypeFromJSON(fieldValue(Field.REPORTED_ENTITY_TYPE)))
        ? [...requiredPayloadFields, Field.REPORTED_ASSET_ID]
        : requiredPayloadFields;

    const invalidFields = validateFieldsOnSubmit(fields, fieldValue, fieldValid);
    if (invalidFields.length === 0 && !!this.props.userInfo.data?.country) {
      this.handleAPICall(formData, isInternalReport);
    } else {
      this.onInvalidFields(invalidFields);
      this.onError(ErrorMessage.FORM_INVALID);
    }
  };

  handleAPICall = async (formData: FormData, isInternalReport: boolean) => {
    const { client, platform, context } = this.props;
    const { data } = this.props.userInfo;
    const market = data?.country || '';

    const fieldValue: (field: Field) => string = getFieldValue(formData);

    const queryParams = {
      platform,
      contextUri: context.contextUri?.toURI(),
      playbackTimestamp: context.playbackTimestamp,
    };

    if (isInternalReport) {
      const request = buildEmployeeRequest(fieldValue, market, undefined, queryParams);
      this.setState({ loading: true });
      try {
        await client.submitEmployeeContentPolicyReport(request);
        this.onSuccess();
      } catch (e) {
        this.onError(ErrorMessage.GENERIC);
      }
    } else {
      const request = buildExternalRequest(fieldValue, market, undefined, queryParams);

      this.setState({ loading: true });
      try {
        await client.submitExternalContentPolicyReport(request);
        this.onSuccess();
      } catch (e) {
        this.onError(ErrorMessage.GENERIC);
      }
    }
  };

  handleCancelEmailVerification = () => {
    this.setState({
      currentStep: Step.STEP1_DESCRIPTION,
      shouldDisplayEmailVerificationFlow: false,
    });
  };

  handleCancelRateLimitErrorDialog = () => {
    this.setState({
      shouldDisplayRateLimitError: false,
    });
  };

  handleCancelUserCountryErrorDialog = () => {
    this.setState({
      shouldDisplayUserCountryError: false,
    });
    this.goToNextStep(Step.STEP1_DESCRIPTION);
  };

  handleCaptchaChange = (token: string | null) => {
    const valid: boolean = !!token;
    const value: string = token || '';
    // If the reCAPTCHA challenge expires the token we receive will be null.
    // Make sure we invalidate the form data for the CAPTCHA token in that case.
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [Field.VERIFICATION_CAPTCHA_TOKEN]: {
          value: value,
          valid: valid,
        },
      },
    }));
  };

  onSuccess = () => {
    this.setState({
      loading: false,
      formSubmissionError: '',
      formSubmissionSuccess: true,
    });
    this.props.userInfo.setRefreshReporterInfo(!this.props.userInfo.refreshReporterInfo);
  };

  onError = (error: ErrorMessage) => {
    this.setState({
      loading: false,
      formSubmissionError: error,
      forceValidation: true,
    });
  };

  onInvalidFields = (invalidFields: Array<Field>) => {
    invalidFields.forEach((field: Field) =>
      this.setState(
        (prevState: Readonly<ContentPolicyReportingState>): ContentPolicyReportingState => ({
          ...prevState,
          formData: {
            ...prevState.formData,
            [field]: {
              valid: false,
              value: prevState.formData[field] ? prevState.formData[field].value : '',
            },
          },
        }),
      ),
    );
  };

  onClickDescriptionShowEmailModal = () => {
    const isRateLimited = this.props.userInfo.isRateLimited;

    if (isRateLimited) {
      this.setState({ shouldDisplayRateLimitError: true });
      return;
    }
    this.setState({ shouldDisplayEmailVerificationFlow: true });
  };

  onClickExternalReportedContentNextStep = () => {
    const { formData } = this.state;
    this.handleFormSubmit(formData, false);
  };

  onClickEmployeeReportedContentNextStep = () => {
    const { formData } = this.state;
    this.handleFormSubmit(formData, true);
  };

  onSuccessfulEmailVerification = (email: string, resourceUri: string, code: string, emailType: EmailType) => {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [Field.REPORTER_EMAIL]: {
          value: email,
          valid: true,
        },
        [Field.VERIFICATION_RESOURCE_URI]: {
          value: resourceUri,
          valid: true,
        },
        [Field.VERIFICATION_CODE]: {
          value: code,
          valid: true,
        },
        [Field.USERNAME]: {
          value: this.props.userInfo.username ?? '',
          valid: true,
        },
      },
    }));

    this.goToReportedContentStep(emailType);
  };

  goToReportedContentStep = (emailType: EmailType) => {
    const isUserCountryError = this.props.userInfo.isUserCountryError;

    this.setState({ shouldDisplayEmailVerificationFlow: false });
    if (emailType === EmailType.EMAIL_TYPE_SPOTIFY) {
      this.goToNextStep(Step.STEP2_EMPLOYEE_REPORTED_CONTENT);
    } else {
      this.goToNextStep(Step.STEP2_EXTERNAL_REPORTED_CONTENT);
    }

    if (isUserCountryError) {
      this.setState({ shouldDisplayUserCountryError: true });
    }
  };

  goToNextStep = (nextStep: Step) => {
    this.setState({
      currentStep: nextStep,
      formSubmissionError: '',
      forceValidation: false,
    });
  };

  setLoading = (loading: boolean) => {
    this.setState({ loading: loading });
  };

  renderErrorMessage = () => {
    const { formSubmissionError, loading } = this.state;
    const { t } = this.props;

    if (formSubmissionError && !loading) {
      return (
        <>
          <Banner colorSet="negative">{t(formSubmissionError)}</Banner>
          <br />
        </>
      );
    }

    return null;
  };

  render() {
    const { transitionTime, client, location } = this.props;

    const {
      formData,
      currentStep,
      shouldDisplayEmailVerificationFlow,
      shouldDisplayRateLimitError,
      forceValidation,
      formSubmissionSuccess,
      loading,
    } = this.state;

    const fieldValue: (identifier: string) => string = getFieldValue(formData);
    const fieldValid: (identifier: string) => boolean = getFieldValid(formData);

    const getStepComponent = (step: Step) => {
      switch (step) {
        case Step.STEP2_EXTERNAL_REPORTED_CONTENT:
          return (
            <Step2ExternalReportedContent
              onStepSubmit={this.onClickExternalReportedContentNextStep}
              onFieldChange={this.handleFieldChange}
              forceValidation={forceValidation}
              onCaptchaChange={this.handleCaptchaChange}
              fieldValue={fieldValue}
              fieldValid={fieldValid}
              loading={loading}
              location={location}
              setLoading={this.setLoading}
            />
          );
        case Step.STEP2_EMPLOYEE_REPORTED_CONTENT:
          return (
            <Step2EmployeeReportedContent
              onStepSubmit={this.onClickEmployeeReportedContentNextStep}
              onFieldChange={this.handleFieldChange}
              forceValidation={forceValidation}
              fieldValue={fieldValue}
              fieldValid={fieldValid}
              loading={loading}
              location={location}
              setLoading={this.setLoading}
            />
          );
        default:
          return (
            <Step1Description
              onStepSubmit={this.onClickDescriptionShowEmailModal}
              onFieldChange={this.handleFieldChange}
              forceValidation={forceValidation}
              loading={loading}
              location={location}
              setLoading={this.setLoading}
            />
          );
      }
    };

    const wasFormSubmittedWithSuccess: boolean = formSubmissionSuccess && !loading;

    if (wasFormSubmittedWithSuccess) {
      this.props.history.push({
        pathname: getThankYouPagePathName(location.pathname),
        search: `?uri=${fieldValue(Field.REPORTED_CONTENT_URIS)}`,
      });
    }

    return (
      <>
        {!wasFormSubmittedWithSuccess && (
          <>
            <LoggedInBanner platform={this.props.platform} />
            {this.renderErrorMessage()}
            {getStepComponent(currentStep)}
          </>
        )}
        {shouldDisplayEmailVerificationFlow && (
          <EmailVerificationDialog
            onSuccessfulFinish={this.onSuccessfulEmailVerification}
            client={client}
            onCancel={this.handleCancelEmailVerification}
            timeout={FASTER_EMAIL_VERIFICATION ? 0 : transitionTime}
            userAccountEmail={this.props.userInfo.userAccountEmail}
          />
        )}
        {shouldDisplayRateLimitError && <RateLimitingDialog onCancel={this.handleCancelRateLimitErrorDialog} />}
      </>
    );
  }
}

const ContentPolicyReportingWithRouter = withRouter(ContentPolicyReporting);
const ContentPolicyReportingWithTintinClient = withTintinClient(ContentPolicyReportingWithRouter);
const ContentPolicyReportingWithMarketInfo = withMarketInfo(ContentPolicyReportingWithTintinClient);
const ContentPolicyReportingWithTranslation = withTranslation('content_policy_reporting')(
  ContentPolicyReportingWithMarketInfo,
);
const ContentPolicyReportingWithContextualInfo = withContextualInfo(ContentPolicyReportingWithTranslation);
const ContentPolicyReportingWithUserInfo = withUserInfo(ContentPolicyReportingWithContextualInfo);
const ContentPolicyReportingWithRemoteProperties = withExperimentation(ContentPolicyReportingWithUserInfo);

export default ContentPolicyReportingWithRemoteProperties;
